import { useEffect, useRef, useState } from 'react';
import { useParams } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import { SignaturePadField, type SignaturePadFieldHandle } from '@/components/SignaturePadField';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LetterOfAuthority } from '@shared/schema';
import { AlertCircle, Check, FileSignature, Pen, Save, Trash2 } from 'lucide-react';

export default function LoaPublicSignPage() {
  const { token } = useParams<{ token: string }>();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [signedLoaData, setSignedLoaData] = useState<LetterOfAuthority | null>(null);
  const signatureRef = useRef<SignaturePadFieldHandle>(null);
  const [signatureImage, setSignatureImage] = useState<string>('');
  const [signatureError, setSignatureError] = useState<string | null>(null);

  const readSignatureImage = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setSignatureError('Please provide a signature');
      return null;
    }

    const dataURL = signatureRef.current.toDataURL('image/png');
    setSignatureImage(dataURL);
    setSignatureError(null);
    return dataURL;
  };

  // Mutation to submit a signed LOA
  const signLoaMutation = useMutation({
    mutationFn: async (signatureImage: string) => {
      const response = await fetch(`/api/public/letters-of-authority/token/${token}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signatureImage }),
        credentials: 'include',
      });

      if (!response.ok) {
        let message = response.statusText || 'Failed to submit your signature';
        try {
          const errorData = await response.json();
          message = errorData.error || errorData.message || message;
        } catch {
          // Non-JSON edge/proxy response; keep the HTTP status visible to the customer.
        }
        throw new Error(`${message} (HTTP ${response.status})`);
      }

      return response.json();
    },
    onSuccess: (updatedLoa) => {
      // Store the updated LOA data with signature info
      setSignedLoaData(updatedLoa);
      setSuccess(true);
      setError(null);

      // Update the query cache to prevent any refetch issues
      queryClient.setQueryData(['/api/public/letters-of-authority/token', token], updatedLoa);

      // Scroll to top for better UX
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    },
    onError: (error: unknown) => {
      // Only set error state if we're not already in a success state
      if (!success && !signedLoaData) {
        if (error instanceof Error) {
          if (error.message.includes('404')) {
            setError('The Letter of Authority could not be found. The link may have expired.');
          } else if (error.message.includes('already signed')) {
            setError('This Letter of Authority has already been signed.');
          } else {
            setError(`Failed to submit your signature: ${error.message}`);
          }
        } else {
          setError('Failed to submit your signature. Please check your connection and try again.');
        }
      }
    },
  });

  // Fetch LOA data using the token
  const {
    data: loa,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['/api/public/letters-of-authority/token', token],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/public/letters-of-authority/token/${token}`);
      return response;
    },
    // Disable automatic refetching after signing to prevent errors
    enabled:
      !!token && !success && !signedLoaData && !signLoaMutation.isSuccess && !signLoaMutation.data,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry if we already have signed data or success state or mutation data
      if (success || signedLoaData || signLoaMutation.isSuccess || signLoaMutation.data) {
        return false;
      }
      // Only retry 2 times for actual network errors
      return failureCount < 2 && error?.status !== 404;
    },
  });

  // Handle query errors separately from component error state
  useEffect(() => {
    // Only show query errors if we haven't successfully signed and don't have mutation data
    if (
      queryError &&
      !success &&
      !signedLoaData &&
      !isLoading &&
      !signLoaMutation.isSuccess &&
      !signLoaMutation.data
    ) {
      if (queryError?.status === 404) {
        setError(
          'The link you followed is invalid or has expired. Please contact DomElec Electrical & Solar Ltd for assistance.'
        );
      } else {
        setError('Unable to load Letter of Authority. Please check your connection and try again.');
      }
    }
    // Clear error if we have successful mutation data
    if ((signLoaMutation.isSuccess || signLoaMutation.data) && error) {
      setError(null);
    }
  }, [
    queryError,
    success,
    signedLoaData,
    isLoading,
    signLoaMutation.isSuccess,
    signLoaMutation.data,
    error,
  ]);

  // Check if LOA is already signed
  useEffect(() => {
    if (loa && loa.status === 'signed' && !success) {
      setSignedLoaData(loa);
      setSuccess(true);
      setError(null);
    }
  }, [loa, success]);

  // Handle signature actions
  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setSignatureImage('');
      setSignatureError(null);
    }
  };

  const saveSignature = () => {
    readSignatureImage();
  };

  // Handle submission
  const handleSubmit = () => {
    const imageToSubmit = readSignatureImage();
    if (!imageToSubmit) {
      setSignatureError('Please sign before submitting');
      return;
    }

    signLoaMutation.mutate(imageToSubmit);
  };

  // Format dates for display
  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Don't show error if we've successfully signed or are in process of signing
  // Also check if mutation data exists (indicates successful signing)
  if (
    error &&
    !isLoading &&
    !success &&
    !signLoaMutation.isPending &&
    !signedLoaData &&
    !signLoaMutation.isSuccess &&
    !signLoaMutation.data
  ) {
    return (
      <div className="container py-10">
        <Card className="max-w-3xl mx-auto">
          <CardHeader className="border-b pb-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="text-destructive h-5 w-5" />
                <CardTitle>Error Loading Letter of Authority</CardTitle>
              </div>
              <Badge variant="destructive">Invalid Link</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="bg-destructive/10 p-4 rounded-md border border-destructive/20 mb-6">
              <p className="text-destructive font-medium">{error}</p>
            </div>
            <div className="flex flex-col space-y-2">
              <p>Possible reasons for this error:</p>
              <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                <li>The link has expired (links are valid for a limited time)</li>
                <li>The Letter of Authority has already been signed</li>
                <li>The token in the URL is invalid or was modified</li>
                <li>The Letter of Authority was deleted or cancelled</li>
              </ul>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-3 flex-col items-start">
            <p className="text-sm text-muted-foreground mb-3">
              If you believe this is a mistake, please contact DomElec Electrical & Solar Ltd
              directly.
            </p>
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => (window.location.href = 'https://www.domelec.co.uk/contact-us')}
            >
              <FileSignature className="h-4 w-4" />
              Contact DomElec Support
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Show success screen if we have success state OR signed LOA data OR mutation was successful
  if (
    success ||
    (signedLoaData && signedLoaData.status === 'signed') ||
    signLoaMutation.isSuccess ||
    signLoaMutation.data
  ) {
    // Use the signed LOA data if available, mutation data, or original query data
    const displayLoa = signedLoaData || signLoaMutation.data || loa;

    return (
      <div className="container py-10">
        <Card className="max-w-3xl mx-auto">
          <CardHeader className="border-b pb-3">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <Check className="text-green-600 h-5 w-5" />
                <CardTitle>Letter of Authority Signed</CardTitle>
              </div>
              <Badge className="bg-green-600">Signed Successfully</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-md border border-green-200 dark:border-green-900 mb-6">
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <div className="bg-white dark:bg-slate-800 p-3 rounded-md border">
                  <img
                    src={displayLoa?.signatureImage}
                    alt="Your Digital Signature"
                    className="max-h-20 w-auto"
                  />
                </div>
                <div>
                  <h3 className="text-green-700 dark:text-green-400 font-semibold mb-2">
                    Successfully Signed by {displayLoa?.authorizedPersonName}
                  </h3>
                  <p className="text-green-600 dark:text-green-400">
                    Your Letter of Authority has been successfully signed and recorded. DomElec
                    Electrical & Solar Ltd is now authorised to proceed with your Solar PV project.
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-2 font-medium">
                    Digital signature recorded on:{' '}
                    {formatDate(displayLoa?.dateOfSigning || new Date())}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium dark:text-slate-100">What happens next?</h3>
              <ol className="list-decimal pl-6 space-y-2 dark:text-slate-300">
                <li>
                  A confirmation email with the signed document will be sent to your registered
                  email address
                </li>
                <li>
                  DomElec Electrical & Solar Ltd will contact you to discuss next steps in your
                  solar installation project
                </li>
                <li>Your authorisation is now valid for 12 months from today's date</li>
              </ol>

              <div className="bg-muted dark:bg-slate-800 p-4 rounded-md mt-6 border dark:border-slate-700">
                <p className="text-sm dark:text-slate-300">
                  This Letter of Authority has been digitally signed and is legally binding. If you
                  have any questions about this process or your solar project, please contact
                  DomElec Electrical & Solar Ltd directly.
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t pt-4 flex justify-center">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => (window.location.href = 'https://domelec.co.uk')}
            >
              Return to DomElec Website
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-10">
      <Card className="max-w-4xl mx-auto">
        <CardHeader className="border-b pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <div>
              <div className="flex items-center gap-2">
                <img
                  src="/images/domelec-logo-new-official.png"
                  alt="DomElec Electrical & Solar Ltd Logo"
                  className="h-8 w-auto"
                />
                <CardTitle>Letter of Authority</CardTitle>
              </div>
              <CardDescription className="mt-1">
                Please review and digitally sign this document
              </CardDescription>
            </div>
            <Badge variant="outline" className="mt-2 sm:mt-0">
              {loa?.status === 'draft'
                ? 'Draft'
                : loa?.status === 'sent'
                  ? 'Awaiting Signature'
                  : loa?.status === 'signed'
                    ? 'Signed'
                    : 'Pending'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="flex flex-col items-center">
                <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mb-3"></div>
                <p>Loading Letter of Authority...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-muted p-6 rounded-md border">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
                  <img
                    src="/images/domelec-logo-new-official.png"
                    alt="DomElec Electrical & Solar Ltd Logo"
                    className="h-16 w-auto"
                  />
                  <div className="text-right">
                    <h2 className="text-xl font-bold">LETTER OF AUTHORITY</h2>
                    <p className="text-sm">Reference: LOA-{loa?.id}</p>
                    <p className="text-sm text-muted-foreground">Date: {formatDate(new Date())}</p>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800 p-4 rounded border mb-6">
                  <p className="mb-4 text-slate-900 dark:text-slate-100">
                    I, <span className="font-semibold">{loa?.authorizedPersonName}</span>,
                    {loa?.customerType === 'commercial' ? (
                      <>
                        {' '}
                        in my capacity as{' '}
                        <span className="font-semibold">{loa?.authorizedPersonTitle}</span> of{' '}
                        <span className="font-semibold">{loa?.companyName}</span>
                      </>
                    ) : (
                      <> as the homeowner</>
                    )}{' '}
                    located at <span className="font-semibold">{loa?.companyAddress}</span>, hereby
                    authorise DomElec Electrical & Solar Ltd to:
                  </p>

                  <ul className="list-disc pl-6 mb-4 space-y-2 text-slate-900 dark:text-slate-100">
                    {loa?.customerType === 'commercial' ? (
                      <>
                        <li>Develop Solar PV plans and proposals for our company premises</li>
                        <li>Submit grid connection applications on our company's behalf</li>
                        <li>
                          Communicate with energy suppliers, distribution network operators, and
                          other relevant parties
                        </li>
                        <li>
                          Access necessary information about our company's energy supply and
                          consumption
                        </li>
                        <li>
                          Submit necessary documentation for renewable energy incentives and
                          certification schemes
                        </li>
                      </>
                    ) : (
                      <>
                        <li>Develop Solar PV plans and proposals for my property</li>
                        <li>Submit grid connection applications on my behalf</li>
                        <li>
                          Communicate with energy suppliers, distribution network operators, and
                          other relevant parties
                        </li>
                        <li>
                          Access necessary information about my property's energy supply and
                          consumption
                        </li>
                        <li>
                          Submit necessary documentation for renewable energy incentives and
                          certification schemes
                        </li>
                      </>
                    )}
                  </ul>

                  <p className="text-slate-900 dark:text-slate-100">
                    This authorisation is valid for a period of 12 months from the date of signing.
                  </p>
                </div>
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Pen className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold text-lg">Digital Signature</h3>
                </div>
                <p className="text-muted-foreground">
                  Please sign below to authorise DomElec Electrical & Solar Ltd to act on
                  {loa?.customerType === 'commercial' ? " your company's" : ' your'} behalf:
                </p>

                <div className="border border-input rounded-md p-3 bg-gray-50">
                  <SignaturePadField
                    ref={signatureRef}
                    penColor="black"
                    className="signature-canvas w-full h-40"
                    style={{
                      background: 'white',
                      border: '1px solid #eee',
                      touchAction: 'none',
                      WebkitUserSelect: 'none',
                      userSelect: 'none',
                      WebkitTouchCallout: 'none',
                    }}
                  />
                </div>

                {signatureError && (
                  <div className="bg-destructive/10 p-3 rounded-md border border-destructive/20">
                    <p className="text-sm text-destructive flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      {signatureError}
                    </p>
                  </div>
                )}

                <div className="flex gap-3 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={clearSignature}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Clear Signature
                  </Button>
                  <Button type="button" onClick={saveSignature} className="gap-2">
                    <Save className="h-4 w-4" />
                    Save Signature
                  </Button>
                </div>

                {signatureImage && (
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md border border-green-200 dark:border-green-800 flex items-center gap-3">
                    <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <div>
                      <p className="text-green-700 dark:text-green-400 font-medium">
                        Signature saved successfully
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-500">
                        You can now submit the Letter of Authority.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="border-t pt-4 flex flex-col sm:flex-row justify-between">
          <p className="text-sm text-muted-foreground mb-4 sm:mb-0 max-w-md">
            By signing, you confirm that you are authorised to act on
            {loa?.customerType === 'commercial' ? ' behalf of the company' : ' your own behalf'}
            and agree to the terms outlined in this Letter of Authority.
          </p>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || signLoaMutation.isPending}
            className="gap-2 sm:min-w-[200px]"
          >
            {signLoaMutation.isPending ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                Submitting...
              </>
            ) : (
              <>
                <FileSignature className="h-4 w-4" />
                Submit Letter of Authority
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
